import * as React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import GroupsIcon from "@mui/icons-material/Groups";
import CampaignIcon from "@mui/icons-material/Campaign";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HubIcon from "@mui/icons-material/Hub";
import BrushIcon from "@mui/icons-material/Brush";
import {
  marketingColors,
  marketingFonts,
  heroBackgroundSx,
  sectionLabelSx,
  sectionTitleSx,
  featureCardSx,
  primaryButtonSx,
} from "../../app/components/tickitMarketingTheme";

const live = [
  {
    icon: <EventAvailableIcon sx={{ fontSize: 28 }} />,
    title: "Event discovery & search",
    body: "Browse and filter upcoming events with date range search — built for guests and planners promoting public listings.",
  },
  {
    icon: <ConfirmationNumberIcon sx={{ fontSize: 28 }} />,
    title: "Ticketing & checkout",
    body: "Ticket categories, cart, and payment flows including card checkout and manual transfer where enabled.",
  },
  {
    icon: <MailOutlineIcon sx={{ fontSize: 28 }} />,
    title: "Invitations",
    body: "Guests can accept or decline digital invitations; organizers run invitation workflows from the dashboard.",
  },
  {
    icon: <QrCodeScannerIcon sx={{ fontSize: 28 }} />,
    title: "QR tickets & door scanning",
    body: "Downloadable tickets with QR codes and a dedicated usher scanner experience for check-in at the door.",
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 28 }} />,
    title: "Partner & admin operations",
    body: "Partner accounts for hosts, plus admin tools for events, ticket requests, campaigns, and live event views.",
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 28 }} />,
    title: "WhatsApp templates & campaigns",
    body: "Create templates and campaigns to reach guests on WhatsApp — orchestrated from the admin console.",
  },
];

const soon = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 28 }} />,
    title: "AI attendance & seating",
    body: "Forecast turnout and auto-assist seating plans — on the roadmap.",
  },
  {
    icon: <HubIcon sx={{ fontSize: 28 }} />,
    title: "Public API & no-code integrations",
    body: "Partner APIs, webhooks, and Zapier-style connectors for your stack — coming later.",
  },
  {
    icon: <BrushIcon sx={{ fontSize: 28 }} />,
    title: "Full white-label & custom domains",
    body: "Bring your domain and complete visual white-label — planned.",
  },
];

export default function Features() {
  const navigate = useNavigate();

  return (
    <Box sx={{ ...heroBackgroundSx, minHeight: "100%", pb: 10 }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, position: "relative", zIndex: 1 }}>
        <Box sx={{ maxWidth: 720, mb: 6 }}>
          <Typography sx={sectionLabelSx}>Product</Typography>
          <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: "2rem", md: "2.75rem" }, mb: 2 }}>
            Built for event planners — honest roadmap
          </Typography>
          <Typography
            sx={{
              fontFamily: marketingFonts.body,
              fontSize: { xs: "1rem", md: "1.1rem" },
              color: marketingColors.gray500,
              lineHeight: 1.75,
              mb: 3,
            }}
          >
            Tickit helps you sell tickets, manage invitations, and run the door. Below is what works in the product
            today versus what is still on the way.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/events/request")}
              sx={primaryButtonSx}
            >
              Plan a wedding or private event
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate("/events")} sx={{ textTransform: "none", borderRadius: "12px", fontWeight: 700 }}>
              Browse events
            </Button>
          </Stack>
        </Box>

        <Typography sx={{ ...sectionLabelSx, mb: 2 }}>Available now</Typography>
        <Grid container spacing={2} sx={{ mb: 6 }}>
          {live.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.title}>
              <Box sx={featureCardSx}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "12px",
                    background: marketingColors.p100,
                    border: `1px solid ${marketingColors.p200}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: marketingColors.p700,
                    mb: 2.5,
                  }}
                >
                  {f.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: marketingFonts.display,
                    fontWeight: 800,
                    fontSize: "1.05rem",
                    color: marketingColors.gray900,
                    mb: 1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {f.title}
                </Typography>
                <Typography sx={{ fontFamily: marketingFonts.body, fontSize: "0.9rem", color: marketingColors.gray500, lineHeight: 1.65 }}>
                  {f.body}
                </Typography>
                <Chip
                  label="Live"
                  size="small"
                  sx={{
                    mt: 2,
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    background: marketingColors.greenL,
                    color: marketingColors.green,
                    border: "none",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography sx={{ ...sectionLabelSx, mb: 2 }}>Coming soon</Typography>
        <Grid container spacing={2}>
          {soon.map((f) => (
            <Grid item xs={12} md={4} key={f.title}>
              <Box sx={{ ...featureCardSx, opacity: 0.92 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "12px",
                    background: marketingColors.gray100,
                    border: `1px solid ${marketingColors.gray200}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: marketingColors.gray500,
                    mb: 2.5,
                  }}
                >
                  {f.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: marketingFonts.display,
                    fontWeight: 800,
                    fontSize: "1.05rem",
                    color: marketingColors.gray700,
                    mb: 1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {f.title}
                </Typography>
                <Typography sx={{ fontFamily: marketingFonts.body, fontSize: "0.9rem", color: marketingColors.gray500, lineHeight: 1.65 }}>
                  {f.body}
                </Typography>
                <Chip
                  label="Coming soon"
                  size="small"
                  sx={{
                    mt: 2,
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    background: marketingColors.gray200,
                    color: marketingColors.gray600,
                    border: "none",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
